import { normalizeCancelReason } from "./cancelReason";
import { parseDivertIcao, shortDivertReason } from "./diversion";
import { DEFAULT_FLIGHT_LOG_KEEP, parseFlightLogKeepList } from "./flightLogClear";
import { spokenTripLabel } from "./flightNumber";
import { simbriefGenerateForMission } from "./flightplan";
import { createManualCharterRequest } from "./inbox";
import { screenOnDemandCharter } from "./onDemandCharter";
import { MEL_SEVERITY_LABEL } from "./mel";
import {
  FERRY_REVENUE_QUOTE_FACTOR,
  ferryToRevenueConfirmCopy,
  isOpenOpsFerry,
  planOpsFerry,
  spokenFlightKind,
} from "./opsFerry";
import { resolveAirportRunway } from "./airportRunways";
import { isReleasePackStatus, RELEASE_PACK_STATUS_LABEL, buildReleasePack } from "./releasePack";
import {
  blankTripSupportLine,
  hydrateTripSupport,
  isTripSupportServiceType,
  isTripSupportStatus,
  TRIP_SUPPORT_SERVICE_LABEL,
  TRIP_SUPPORT_STATUS_LABEL,
  tripSupportOf,
} from "./tripSupport";
import type {
  AircraftSpec,
  CancelReasonCode,
  Mission,
  ReleasePackStatus,
  SceneryAirport,
  TripSupportLine,
  TripSupportServiceType,
  TripSupportStatus,
} from "./types";
import {
  applyAllSessionPending,
  confirmSessionPending,
  enqueuePending,
  isSpokenYes,
  rejectSessionPending,
  type VoiceToolSession,
} from "./voiceApply";
import { contentXmlScheduleBlocked } from "./contentXmlSchedule";
import { isSelfCrewHint, resolveCompanyCrew } from "./crewReassign";
import {
  findBoardTrip,
  findCharterOnDesk,
  findTail,
  focusRouteFromSnapshot,
  leanTripSupportLine,
  parseRouteHint,
  pickSpeakableBoardTrips,
  resolveBoardTrip,
  resolveSimbriefTicket,
  speakableBoardNote,
  type BoardTripHint,
  type VoiceDeskSnapshot,
} from "./voiceDesk";
import {
  liveVoiceInbox,
  resolveVoiceInboxRow,
  spokenInboxRef,
  type VoiceInboxHint,
} from "./voiceLookup";

export const VOICE_TOOL_NAMES = [
  "get_inbox",
  "get_board",
  "get_fleet",
  "get_mel",
  "get_mx",
  "get_ledger",
  "get_flight_logs",
  "get_duty",
  "get_destinations",
  "get_sync",
  "get_tracker",
  "get_weather",
  "get_notams",
  "get_charter_rundown",
  "get_current_flight",
  "get_conflicts",
  "get_trip_support",
  "get_release_pack",
  "get_ofp",
  "get_aircraft_state",
  "get_assignments",
  "can_assign",
  "list_pending",
  "propose_create_charter",
  "propose_mint_on_demand_charter",
  "propose_accept_job",
  "propose_decline_job",
  "propose_clear_inbox",
  "propose_remove_inbox",
  "propose_assign_trip",
  "propose_assign_tail",
  "propose_reassign_crew",
  "propose_schedule_std",
  "propose_schedule_eta",
  "propose_update_pax",
  "propose_update_trip_support",
  "propose_update_release_pack",
  "propose_open_simbrief",
  "propose_attach_ofp",
  "propose_activate_flight",
  "propose_update_trip_status",
  "propose_divert_trip",
  "propose_reinstate_trip",
  "propose_schedule_ferry",
  "propose_ferry_to_revenue",
  "propose_remove_trip",
  "propose_clear_flight_log",
  "propose_respond_assignment",
  "propose_mel_ground",
  "propose_file_mel",
  "propose_close_mel",
  "propose_file_squawk",
  "propose_close_squawk",
  "propose_send_to_mx",
  "propose_close_mx",
  "propose_fleet_status",
  "propose_arm_standby",
  "propose_disarm_standby",
  "propose_capital_adjust",
  "propose_push_sync",
  "propose_pull_sync",
  "confirm_pending",
  "apply_pending",
  "reject_pending",
] as const;

export type VoiceToolName = (typeof VOICE_TOOL_NAMES)[number];

export type VoiceTripStatus = "pending" | "in-progress" | "completed" | "cancelled";

export type VoiceAssignmentStatus = "accepted" | "declined" | "held";
export type VoiceMelSeverity = "info" | "defer" | "ground";
export type VoiceSquawkSeverity = "info" | "defer" | "ground";
export type VoiceFleetStatus = "available" | "grounded" | "ferry" | "in_mx";
export type VoiceClearMode = "decline" | "remove";

export type VoicePendingAction =
  | { type: "accept_job"; requestId: string; summary: string; id?: string }
  | { type: "decline_job"; requestId: string; summary: string; id?: string }
  | { type: "clear_inbox"; requestIds: string[]; mode: VoiceClearMode; summary: string; id?: string }
  | { type: "remove_inbox"; requestId: string; summary: string; id?: string }
  | { type: "assign_tail"; missionId: string; aircraftId: string; requestId?: string; summary: string; id?: string }
  | {
      type: "reassign_crew";
      missionId: string;
      crewHint: string;
      accept?: boolean;
      acceptMissingScenery?: boolean;
      requestId?: string;
      summary: string;
      id?: string;
    }
  | { type: "schedule_std"; missionId?: string; departureIso: string; requestId?: string; summary: string; id?: string }
  | { type: "schedule_eta"; missionId: string; arrivalIso: string; requestId?: string; summary: string; id?: string }
  | { type: "update_pax"; missionId?: string; requestId?: string; paxCount?: number; cargoLb?: number; summary: string; id?: string }
  | { type: "activate_flight"; missionId: string; requestId?: string; summary: string; id?: string }
  | {
      type: "update_trip_status";
      missionId: string;
      status: VoiceTripStatus;
      requestId?: string;
      cancelReason?: CancelReasonCode;
      cancelReasonNote?: string;
      summary: string;
      id?: string;
    }
  | {
      type: "divert_trip";
      missionId: string;
      toIcao: string;
      reason: string;
      phase: "diverting" | "diverted";
      requestId?: string;
      summary: string;
      id?: string;
    }
  | {
      type: "reinstate_trip";
      missionId: string;
      actor: "desk" | "megan";
      summary: string;
      id?: string;
    }
  | {
      type: "schedule_ferry";
      originIcao: string;
      destIcao: string;
      departureIso: string;
      aircraftId?: string;
      registration?: string;
      crewId?: string;
      mode: "create" | "reinstate";
      missionId?: string;
      summary: string;
      id?: string;
    }
  | {
      type: "ferry_to_revenue";
      missionId: string;
      paxCount: number;
      note?: string;
      summary: string;
      id?: string;
    }
  | { type: "remove_trip"; missionId: string; summary: string; id?: string }
  | { type: "clear_flight_log"; keep: string[]; summary: string; id?: string }
  | {
      type: "respond_assignment";
      assignmentId: string;
      status: VoiceAssignmentStatus;
      reasonCode: string;
      comment?: string;
      summary: string;
      id?: string;
    }
  | { type: "apply_mel_ground"; aircraftId: string; description: string; ata?: string; summary: string; id?: string }
  | { type: "file_mel"; aircraftId: string; description: string; severity: VoiceMelSeverity; ata?: string; summary: string; id?: string }
  | { type: "close_mel"; melId: string; summary: string; id?: string }
  | { type: "file_squawk"; aircraftId: string; title: string; severity: VoiceSquawkSeverity; detail?: string; summary: string; id?: string }
  | { type: "close_squawk"; squawkId: string; summary: string; id?: string }
  | { type: "send_to_mx"; aircraftId: string; mroId?: string; summary: string; id?: string }
  | { type: "close_mx"; aircraftId: string; summary: string; id?: string }
  | { type: "fleet_status"; aircraftId: string; status: VoiceFleetStatus; summary: string; id?: string }
  | { type: "arm_standby"; summary: string; id?: string }
  | { type: "disarm_standby"; summary: string; id?: string }
  | { type: "change_capital"; amountCad: number; memo: string; summary: string; id?: string }
  | { type: "push_sync"; summary: string; id?: string }
  | { type: "pull_sync"; summary: string; id?: string }
  | { type: "assign_trip"; requestId: string; aircraftId: string; crewId?: string; summary: string; id?: string }
  | {
      type: "create_charter";
      requestId: string;
      originIcao: string;
      destIcao: string;
      departureIso: string;
      aircraftId: string;
      registration?: string;
      paxCount?: number;
      cargoLb?: number;
      notes?: string;
      clientName?: string;
      fileNow: boolean;
      summary: string;
      id?: string;
    }
  | {
      type: "mint_on_demand_charter";
      destIcao: string;
      originIcao?: string;
      departureIso?: string;
      aircraftId?: string;
      registration?: string;
      paxCount?: number;
      cargoLb?: number;
      notes?: string;
      clientName?: string;
      techStopIcao?: string;
      summary: string;
      id?: string;
    }
  | {
      type: "update_trip_support";
      missionId: string;
      op: "add" | "update" | "remove";
      lineId?: string;
      airportIcao?: string;
      serviceType?: TripSupportServiceType;
      vendorName?: string;
      status?: TripSupportStatus;
      estimatedCost?: number;
      currency?: "CAD" | "USD" | "EUR" | "GBP";
      notes?: string;
      confirmByIso?: string;
      summary: string;
      id?: string;
    }
  | {
      type: "update_release_pack";
      missionId: string;
      status: ReleasePackStatus;
      summary: string;
      id?: string;
    }
  | { type: "open_simbrief"; missionId: string; summary: string; id?: string }
  | { type: "attach_ofp"; missionId: string; summary: string; id?: string };

export const VOICE_TRIP_STATUSES: readonly VoiceTripStatus[] = ["pending", "in-progress", "completed", "cancelled"];

export function parseVoiceTripStatus(raw: string): VoiceTripStatus | "" {
  const key = raw.trim().toLowerCase().replace(/_/g, "-");
  if (key === "scheduled" || key === "filed") return "pending";
  if (key === "active" || key === "progress" || key === "inprogress") return "in-progress";
  if (key === "done" || key === "complete") return "completed";
  if (key === "cancel" || key === "canceled" || key === "dropped") return "cancelled";
  return (VOICE_TRIP_STATUSES as readonly string[]).includes(key) ? (key as VoiceTripStatus) : "";
}

export type VoiceToolResult = {
  ok: boolean;
  name: VoiceToolName;
  data?: unknown;
  error?: string;
  /** Speak-now copy for the model — empty board / fail closed must not stall. */
  note?: string;
  /** desk = local store; company = Nanode /v1/state; no_company = snapshot missing. */
  source?: "desk" | "company" | "no_company";
  requiresConfirmation?: boolean;
  action?: VoicePendingAction;
  actions?: VoicePendingAction[];
  applied?: boolean;
};

export type { VoiceToolSession };

export type GrokToolSpec = {
  type: "function";
  function: {
    name: VoiceToolName;
    description: string;
    parameters: Record<string, unknown>;
  };
};

/** Flat function tools for the xAI Voice Agent `session.update` payload. */
export type VoiceAgentToolSpec = {
  type: "function";
  name: VoiceToolName;
  description: string;
  parameters: Record<string, unknown>;
};

const emptyParams = { type: "object", properties: {}, additionalProperties: false };

export const VOICE_GROK_TOOLS: GrokToolSpec[] = [
  {
    type: "function",
    function: {
      name: "get_inbox",
      description:
        "Live Dispatcher Inbox — same new/assigned RFQs the desk list shows. Declined and already-filed accepts are omitted. Use spokenRef / client / route / id on later writes. Never invent an RFQ.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "get_board",
      description:
        "Live charter board / scheduled trips. Read-only — never wait for a confirm. Prefer open (pending / in-progress) rows. Each row has flightNumber, registration, route, and spokenLabel. Empty board is a valid result: say the board is empty immediately. Always read flight number + route + tail — never the tail alone as the flight id.",
      parameters: emptyParams,
    },
  },
  { type: "function", function: { name: "get_fleet", description: "Fleet status, hours, assignable tails.", parameters: emptyParams } },
  { type: "function", function: { name: "get_mel", description: "Open MEL / deferred items from the desk.", parameters: emptyParams } },
  { type: "function", function: { name: "get_mx", description: "Open squawks and inspection pressure.", parameters: emptyParams } },
  { type: "function", function: { name: "get_ledger", description: "Desk ledger balance and recent lines. Do not invent cash.", parameters: emptyParams } },
  { type: "function", function: { name: "get_flight_logs", description: "Flight log and logbook rows on file.", parameters: emptyParams } },
  { type: "function", function: { name: "get_duty", description: "Crew duty / roster from the company CAR clock (703 / 704 / 705; Celtic Air default 704).", parameters: emptyParams } },
  { type: "function", function: { name: "get_destinations", description: "Approved destinations and company bases, including Jet A ballpark (curated table or Settings override — not Avgas, not a posted FBO invoice).", parameters: emptyParams } },
  { type: "function", function: { name: "get_sync", description: "Sync stub state. Never invent a connection.", parameters: emptyParams } },
  { type: "function", function: { name: "get_tracker", description: "Flight tracker / map companion state.", parameters: emptyParams } },
  { type: "function", function: { name: "get_weather", description: "Live weather brief already on the desk (dep/enroute/dest). Never invent METAR.", parameters: emptyParams } },
  { type: "function", function: { name: "get_notams", description: "NOTAM / TFR slice from the live weather brief.", parameters: emptyParams } },
  {
    type: "function",
    function: {
      name: "get_charter_rundown",
      description: "Charter + Wx + NOTAM + MEL rundown for the focused trip or a named Inbox id.",
      parameters: {
        type: "object",
        properties: {
          requestId: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_current_flight",
      description:
        "Current / OCC ticket on the desk — Mark Active bind wins. Flight#, route, lifecycle, divert, tracker phase when already on file. Altitude / GS / live ETA stay missing unless the tracker already has them. Never invent kinematics. Empty bind is a valid result: say there is no current flight on the desk.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "get_conflicts",
      description:
        "Tail / crew / duty conflicts already scored by detectClashes (same as Schedule Gantt). Read-only. Empty list means none on file. If the clash list is missing, say it is not on the desk — do not invent a conflict.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_trip_support",
      description:
        "Trip Support lines on a board ticket already on file (Handling/FBO, Fuel, Catering, Hotel, Ground, Customs, Other). Same Mission.id / CEL#### / F=ferry as the desk tab. Read-only. If the ticket is missing, say it is not on the desk.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_release_pack",
      description:
        "Digital release pack status and checklist for a board ticket already on file (Draft / Ready / Released). Checklist is derived from fields already on the ticket — never invent OFP, Wx, or MEL. If the ticket is missing, say it is not on the desk.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_ofp",
      description:
        "Read the OFP already attached to a filed ticket. If none is on file, say it is not on the desk. Never invent an OFP number, route, or fuel figure.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          registration: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "list_pending",
      description: "List propose_* writes waiting for a spoken yes on this call.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "get_aircraft_state",
      description: "Automatic aircraft phase from the sim/fusion pipeline. Never invent progress.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "can_assign",
      description: "Score whether a charter can be assigned to a tail (duty, state, double-book). Read-only. Same rules as the desk assign API.",
      parameters: {
        type: "object",
        properties: { requestId: { type: "string" }, aircraftId: { type: "string" }, registration: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_assignments",
      description: "Pending and decided trip assignments / roster replies on file. Read-only.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "propose_assign_trip",
      description: "Propose assigning an Inbox charter to a tail and one pilot using canAssign. One pilot per trip — not shared-cockpit. Confirmation required. Phone/Megan can call this later.",
      parameters: {
        type: "object",
        properties: {
          requestId: { type: "string" },
          aircraftId: { type: "string" },
          registration: { type: "string" },
          crewId: { type: "string" },
        },
        required: ["requestId"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_mint_on_demand_charter",
      description:
        "Propose an on-demand revenue charter from a destination ICAO and file it onto the schedule after a spoken yes. Dest is required. Origin, STD Zulu, pax, tail, and tech-stop ICAO are optional — omit them instead of inventing. Tech stops come from the Content.xml addon list when the city-pair is out of range; pass techStopIcao only when they name one. A ferry is inserted only when the tail is not already at the origin. Same writer as Inbox Create charter. Spoken yes + confirm_pending required. Do not invent an ICAO, time, tail, or flight number.",
      parameters: {
        type: "object",
        properties: {
          destIcao: { type: "string" },
          originIcao: { type: "string" },
          route: { type: "string" },
          departureIso: { type: "string" },
          aircraftId: { type: "string" },
          registration: { type: "string" },
          paxCount: { type: "number" },
          cargoLb: { type: "number" },
          notes: { type: "string" },
          clientName: { type: "string" },
          techStopIcao: { type: "string" },
        },
        required: ["destIcao"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_create_charter",
      description:
        "Propose creating a revenue charter RFQ (same as Inbox Create charter). Needs origin, dest, STD Zulu, and a fleet tail. Optional pax / cargo / client / notes. fileNow true also files to the board after confirm (Create & file). Not a ferry — use propose_schedule_ferry for empty company reposition. Dest-only on-demand filing is propose_mint_on_demand_charter. Spoken yes + confirm_pending required. Do not invent an ICAO, time, or tail.",
      parameters: {
        type: "object",
        properties: {
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          departureIso: { type: "string" },
          aircraftId: { type: "string" },
          registration: { type: "string" },
          paxCount: { type: "number" },
          cargoLb: { type: "number" },
          notes: { type: "string" },
          clientName: { type: "string" },
          fileNow: { type: "boolean" },
        },
        required: ["departureIso"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_accept_job",
      description:
        "Propose accepting / planning a live Inbox charter onto the board (same as Dispatcher File to board). Find by requestId, client, route, or origin/dest. If only one live RFQ, requestId is optional. Does not file until confirm_pending after a spoken yes.",
      parameters: {
        type: "object",
        properties: {
          requestId: { type: "string" },
          client: { type: "string" },
          route: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_decline_job",
      description:
        "Propose declining one live Inbox RFQ (same as Dispatcher Decline — removes it from the live Inbox list). Find by requestId, client, route, or origin/dest. If only one live RFQ, requestId is optional. To empty the whole live Inbox, use propose_clear_inbox. Confirmation required via spoken yes + confirm_pending.",
      parameters: {
        type: "object",
        properties: {
          requestId: { type: "string" },
          client: { type: "string" },
          route: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_clear_inbox",
      description:
        "Propose clearing the live Dispatcher Inbox — decline every remaining new/assigned RFQ (same as working Decline until the list is empty). Use when the caller says clear the inbox / decline them all. Confirmation required. Does not touch Settings.",
      parameters: {
        type: "object",
        properties: { mode: { type: "string", enum: ["decline", "remove"], description: "decline = desk Decline (default). remove = desk Delete request." } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_remove_inbox",
      description:
        "Propose deleting one Inbox row (same as Dispatcher Delete request). Find by requestId, client, or route. Confirmation required.",
      parameters: {
        type: "object",
        properties: {
          requestId: { type: "string" },
          client: { type: "string" },
          route: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_assign_tail",
      description: "Propose assigning a board trip to a tail. Confirmation required via spoken yes + confirm_pending.",
      parameters: {
        type: "object",
        properties: { missionId: { type: "string" }, aircraftId: { type: "string" }, registration: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_reassign_crew",
      description:
        "Propose moving a live CEL#### / F ticket onto another company pilot’s trips (or the caller’s — crewHint me/my). Same-company roster only. Confirmation required via spoken yes + confirm_pending. Do not invent a crew name.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          crewHint: { type: "string" },
          crewId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_schedule_std",
      description:
        "Propose setting or moving STD on a board trip or a live Inbox RFQ already on file. Confirmation required via spoken yes + confirm_pending. Do not invent a time.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          requestId: { type: "string" },
          departureIso: { type: "string" },
          callsign: { type: "string" },
          client: { type: "string" },
          route: { type: "string" },
        },
        required: ["departureIso"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_schedule_eta",
      description:
        "Propose setting or moving STA / ETA on a board trip already on file (arrival ISO-8601). Confirmation required. Do not invent a time.",
      parameters: {
        type: "object",
        properties: { missionId: { type: "string" }, arrivalIso: { type: "string" }, callsign: { type: "string" } },
        required: ["arrivalIso"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_update_pax",
      description:
        "Propose changing souls (pax) and/or cargo lb on a board trip or Inbox RFQ already on file. Last-minute cancels use paxCount 0. Confirmation required. Do not invent a trip or a count.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          requestId: { type: "string" },
          callsign: { type: "string" },
          paxCount: { type: "number" },
          cargoLb: { type: "number" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_update_trip_support",
      description:
        "Propose adding, updating, or removing one Trip Support line on a board ticket already on file (same Mission.id / CEL#### / F=ferry). op add / update / remove. Identify the ticket by flightNumber plus route. Confirmation required. Do not invent a ticket, vendor, or ICAO.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          op: { type: "string", enum: ["add", "update", "remove"] },
          lineId: { type: "string" },
          airportIcao: { type: "string" },
          serviceType: {
            type: "string",
            enum: ["handling", "fuel", "catering", "hotel", "ground", "customs", "other"],
          },
          vendorName: { type: "string" },
          status: { type: "string", enum: ["requested", "confirmed", "cancelled"] },
          estimatedCost: { type: "number" },
          currency: { type: "string", enum: ["CAD", "USD", "EUR", "GBP"] },
          notes: { type: "string" },
          confirmByIso: { type: "string" },
        },
        required: ["op"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_update_release_pack",
      description:
        "Propose setting the digital release pack status on a board ticket already on file: draft, ready, or released. Same Mission.id / CEL#### / F=ferry. Confirmation required. Do not invent a ticket or a status.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          status: { type: "string", enum: ["draft", "ready", "released"] },
        },
        required: ["status"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_open_simbrief",
      description:
        "Propose opening the prefilled SimBrief generate page on the desk PC for a filed ticket. Fields are that ticket's UTC STD, pax, extra cargo only, origin, dest, aircraft type, and registration — the same Flight plans Request new plan. Spoken yes + confirm_pending required. Do not invent a ticket or an OFP.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          registration: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_attach_ofp",
      description:
        "Propose retrieving the latest SimBrief OFP and attaching it to a filed ticket — the same Flight plans Retrieve latest writer. Spoken yes + confirm_pending required. Do not invent an OFP number, route, or fuel. If retrieve fails, say it is not on the desk.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          registration: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_activate_flight",
      description: "Propose activating a filed trip (pending → in progress). Confirmation required via spoken yes + confirm_pending.",
      parameters: {
        type: "object",
        properties: { missionId: { type: "string" }, callsign: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_update_trip_status",
      description:
        "Propose a Dispatcher board status on a trip already on file: pending, in-progress, completed, or cancelled. Confirmation required. Identify the trip by flightNumber (CEL1047) plus route (originIcao–destIcao). Do not invent a trip or a status. Cancelling always needs cancelReason (operational, scheduling, conflict, ferry_sandwich, or other) — other requires cancelReasonNote custom text. To undo a cancel use propose_reinstate_trip.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          status: { type: "string", enum: ["pending", "in-progress", "completed", "cancelled"] },
          cancelReason: {
            type: "string",
            enum: ["operational", "scheduling", "conflict", "ferry_sandwich", "other"],
          },
          cancelReasonNote: { type: "string" },
        },
        required: ["status"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_divert_trip",
      description:
        "Propose a diversion on a trip already on file when the pilot says they are diverting (or have diverted) to an ICAO with a short reason. toIcao is the divert field — destIcao on the ticket stays the original intended dest. phase is diverting (still airborne) or diverted (wheels down away from filed dest). Identify the trip by flightNumber plus original originIcao–destIcao. Spoken yes + confirm_pending required. Do not invent a trip, ICAO, or reason. Do not file a follow-on ferry unless they ask.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          toIcao: { type: "string" },
          reason: { type: "string" },
          phase: { type: "string", enum: ["diverting", "diverted"] },
        },
        required: ["toIcao", "reason"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_reinstate_trip",
      description:
        "Propose restoring a recently cancelled trip to scheduled (same ticket id, times, tail). Identify by flightNumber plus route. Spoken yes + confirm_pending required. Do not invent a trip.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_schedule_ferry",
      description:
        "Propose an ops company ferry from time + origin + dest. Not an Inbox RFQ — never invent a customer request. Pick tail/crew when the board makes them obvious. If a cancelled ferry on that pair still exists, this proposes reinstate (put the ferry back) instead of a new ticket. Spoken yes + confirm_pending required. Respect airport quiet hours — include the clash in the readback if the given STD is illegal.",
      parameters: {
        type: "object",
        properties: {
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          departureIso: { type: "string" },
          aircraftId: { type: "string" },
          registration: { type: "string" },
          crewId: { type: "string" },
          forceCreate: { type: "boolean" },
        },
        required: ["departureIso"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_ferry_to_revenue",
      description:
        "Propose converting an open empty ferry into a light revenue charter when last-minute pax show up. Same flight number / ticket. Needs paxCount (1–2 typical) and optional note. Quoted at the ferry-fill discount vs a normal charter card, CAD. Spoken yes + confirm_pending required. Identify the ferry by flightNumber plus route.",
      parameters: {
        type: "object",
        properties: {
          missionId: { type: "string" },
          flightNumber: { type: "string" },
          callsign: { type: "string" },
          originIcao: { type: "string" },
          destIcao: { type: "string" },
          route: { type: "string" },
          paxCount: { type: "number" },
          note: { type: "string" },
        },
        required: ["paxCount"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_remove_trip",
      description: "Propose deleting a board trip (same as Dispatcher Delete trip). Confirmation required. Do not invent a trip.",
      parameters: {
        type: "object",
        properties: { missionId: { type: "string" }, callsign: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_clear_flight_log",
      description:
        "Propose clearing the Flight Log and the Flight plans board down to a keep list (same as the Dispatcher Clear log… button). Use when the caller says wipe / clear the flight log or the flight plans except certain trips. `keep` is the flight numbers that survive, e.g. [\"CEL1003F\", \"CEL1001\"] — when the caller does not name any, leave it out and the desk keeps its default pair. Everything not on the keep list is deleted and cannot be restored, so confirmation is required. The desk computes which rows go — do not invent flight numbers or counts.",
      parameters: {
        type: "object",
        properties: {
          keep: { type: "array", items: { type: "string" }, description: "Flight numbers to keep, e.g. CEL1003F." },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_respond_assignment",
      description:
        "Propose Accept / Hold / Decline on a pending assignment already on file (same as Dispatcher assignment panel). Confirmation required. Need a reasonCode for accept or decline.",
      parameters: {
        type: "object",
        properties: {
          assignmentId: { type: "string" },
          requestId: { type: "string" },
          status: { type: "string", enum: ["accepted", "declined", "held"] },
          reasonCode: { type: "string", enum: ["cannot_fly_time", "weather", "aircraft", "personal", "defer", "other"] },
          comment: { type: "string" },
        },
        required: ["status"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_mel_ground",
      description: "Propose filing a grounding MEL. Confirmation required via spoken yes + confirm_pending.",
      parameters: {
        type: "object",
        properties: {
          aircraftId: { type: "string" },
          registration: { type: "string" },
          description: { type: "string" },
          ata: { type: "string" },
        },
        required: ["description"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_file_mel",
      description: "Propose filing a MEL (info / defer / ground) on a tail already on file. Confirmation required.",
      parameters: {
        type: "object",
        properties: {
          aircraftId: { type: "string" },
          registration: { type: "string" },
          description: { type: "string" },
          severity: { type: "string", enum: ["info", "defer", "ground"] },
          ata: { type: "string" },
        },
        required: ["description"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_close_mel",
      description: "Propose closing an open MEL already on file. Confirmation required.",
      parameters: {
        type: "object",
        properties: { melId: { type: "string" }, aircraftId: { type: "string" }, registration: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_file_squawk",
      description: "Propose filing a squawk on a tail already on file. Confirmation required.",
      parameters: {
        type: "object",
        properties: {
          aircraftId: { type: "string" },
          registration: { type: "string" },
          title: { type: "string" },
          severity: { type: "string", enum: ["info", "defer", "ground"] },
          detail: { type: "string" },
        },
        required: ["title"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_close_squawk",
      description: "Propose closing an open squawk already on file. Confirmation required.",
      parameters: {
        type: "object",
        properties: { squawkId: { type: "string" }, aircraftId: { type: "string" }, registration: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_send_to_mx",
      description: "Propose sending a tail to mx / ferry (same as Dispatcher Send to mx). Confirmation required. mroId is a company MX base ICAO from Maintenance. Do not invent a shop. If the company has no MX base, say so and do not send.",
      parameters: {
        type: "object",
        properties: {
          aircraftId: { type: "string" },
          registration: { type: "string" },
          mroId: { type: "string" },
        },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_close_mx",
      description: "Propose closing mx on a tail and returning it to the line. Confirmation required.",
      parameters: {
        type: "object",
        properties: { aircraftId: { type: "string" }, registration: { type: "string" } },
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_fleet_status",
      description: "Propose setting a tail status already on the fleet: available, grounded, ferry, or in_mx. Confirmation required. Do not invent a tail.",
      parameters: {
        type: "object",
        properties: {
          aircraftId: { type: "string" },
          registration: { type: "string" },
          status: { type: "string", enum: ["available", "grounded", "ferry", "in_mx"] },
        },
        required: ["status"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_arm_standby",
      description: "Propose arming Duty Standby on the Dashboard (not Settings windows). Confirmation required.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "propose_disarm_standby",
      description: "Propose disarming Duty Standby. Confirmation required.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "propose_capital_adjust",
      description: "Propose a ledger adjustment. Confirmation required via spoken yes + confirm_pending. Do not invent amounts.",
      parameters: {
        type: "object",
        properties: { amountCad: { type: "number" }, memo: { type: "string" } },
        required: ["amountCad"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "propose_push_sync",
      description: "Propose pushing the desk snapshot to the sync stub. Confirmation required via spoken yes + confirm_pending.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "propose_pull_sync",
      description:
        "Propose pulling the live Nanode company snapshot onto this desk (same Pull & replace as Data / Sync). Spoken yes + confirm_pending required. Does not invent tickets. Does not change API keys, the sync token, or Nanode .env.",
      parameters: emptyParams,
    },
  },
  {
    type: "function",
    function: {
      name: "confirm_pending",
      description:
        "Sign / apply one pending write after the caller speaks a clear yes. Same mutation as desk Confirm. Requires spokenYes true. Does nothing if the queue is empty.",
      parameters: {
        type: "object",
        properties: {
          spokenYes: { type: "boolean", description: "Must be true after a spoken yes from the caller." },
          index: { type: "number" },
          type: { type: "string" },
          id: { type: "string" },
        },
        required: ["spokenYes"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "apply_pending",
      description:
        "Sign / apply pending writes after a spoken yes. With index or type, applies one; otherwise applies all. Same mutations as desk Confirm.",
      parameters: {
        type: "object",
        properties: {
          spokenYes: { type: "boolean", description: "Must be true after a spoken yes from the caller." },
          index: { type: "number" },
          type: { type: "string" },
          id: { type: "string" },
        },
        required: ["spokenYes"],
      },
    },
  },
  {
    type: "function",
    function: {
      name: "reject_pending",
      description: "Drop a pending write after the caller declines. Does not mutate the desk.",
      parameters: {
        type: "object",
        properties: { index: { type: "number" }, type: { type: "string" }, id: { type: "string" } },
      },
    },
  },
];

export const VOICE_AGENT_TOOLS: VoiceAgentToolSpec[] = VOICE_GROK_TOOLS.map((t) => ({
  type: "function",
  name: t.function.name,
  description: t.function.description,
  parameters: t.function.parameters,
}));

function tripHintFromArgs(args: Record<string, unknown>): BoardTripHint {
  return {
    missionId: str(args.missionId),
    flightNumber: str(args.flightNumber),
    callsign: str(args.callsign) || str(args.flightNumber),
    registration: str(args.registration),
    originIcao: str(args.originIcao),
    destIcao: str(args.destIcao),
    route: str(args.route),
  };
}

function sceneryFromVoiceSnap(snap: VoiceDeskSnapshot): SceneryAirport[] {
  return (snap.destinations ?? []).map((d) => ({
    id: d.icao,
    icao: d.icao,
    name: d.name,
    city: d.city,
    country: "",
    runwayFt: typeof d.runwayFt === "number" && d.runwayFt > 0 ? d.runwayFt : resolveAirportRunway(d.icao).longestFt,
    notes: "",
  }));
}

function missionsFromVoiceBoard(snap: VoiceDeskSnapshot): Mission[] {
  return snap.board.map((b) => ({
    id: b.id,
    createdAt: b.departureIso || "",
    callsign: b.callsign,
    flightNumber: b.flightNumber,
    aircraftId: b.aircraftId,
    aircraftName: "",
    registration: b.registration,
    flightType: b.flightType ?? "corporate",
    originIcao: b.originIcao,
    destIcao: b.destIcao,
    originName: "",
    destName: "",
    originCity: "",
    destCity: "",
    distanceNm: 0,
    eteMin: b.eteMin ?? 0,
    fuelGal: 0,
    fuelLbs: 0,
    trackTrue: 0,
    paxCount: b.pax ?? 0,
    paxNames: [],
    cargoLb: b.cargoLb,
    scenario: "",
    purpose: b.purpose ?? "",
    departureIso: b.departureIso,
    weatherNote: "",
    originMetar: "",
    destMetar: "",
    status: b.status,
    isMaintenance: b.isMaintenance,
    originRunwayFt: 0,
    destRunwayFt: 0,
    legs: [],
    source: "manual",
    tripSupport: Array.isArray(b.tripSupport)
      ? hydrateTripSupport(b.tripSupport)
      : [],
    releasePack: b.releasePack
      ? { status: b.releasePack.status, updatedAtUtc: b.releasePack.updatedAtUtc, releasedAtUtc: b.releasePack.releasedAtUtc }
      : undefined,
  }));
}

function regKey(value: string | undefined): string {
  return (value ?? "").toUpperCase().replace(/\s+/g, "");
}

function fleetForSimbriefTicket(
  snap: VoiceDeskSnapshot,
  trip: { aircraftId?: string; registration?: string },
): AircraftSpec[] {
  const book = snap.liveFleet ?? [];
  const wantReg = regKey(trip.registration);
  const live =
    book.find((row) => row.id === trip.aircraftId) ??
    (wantReg ? book.find((row) => regKey(row.registration) === wantReg) : undefined);
  if (!live) return [];
  return [
    {
      ...live,
      registration: trip.registration || live.registration,
    },
  ];
}

function gatedWrite(
  name: VoiceToolName,
  _snap: VoiceDeskSnapshot,
  action: VoicePendingAction,
  session?: VoiceToolSession,
): VoiceToolResult {
  // Megan is the dispatch proxy. Desk UI role (Pilot vs Dispatcher) does not
  // gate Inbox / board / ferry / cancel / retime / pax / fleet / Mx writes.
  // Settings / wipe / account admin are not in VOICE_TOOL_NAMES.
  void _snap;
  const queued = enqueuePending(session, action);
  return { ok: true, name, requiresConfirmation: true, action: queued, data: { pending: true, summary: queued.summary } };
}

function requireSpokenYes(name: VoiceToolName, args: Record<string, unknown>): VoiceToolResult | null {
  if (isSpokenYes(args)) return null;
  return {
    ok: false,
    name,
    error: "Need a spoken yes from the caller before applying. Propose first, ask them to confirm on the call, then set spokenYes true.",
  };
}

function parseArgs(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === "object") return raw as Record<string, unknown>;
  return {};
}

export function isVoiceToolName(name: string): name is VoiceToolName {
  return (VOICE_TOOL_NAMES as readonly string[]).includes(name);
}

/** Execute one grounded tool against live snapshot. Never invents flights / money / Wx. */
export function executeVoiceTool(
  name: string,
  rawArgs: unknown,
  snap: VoiceDeskSnapshot,
  session?: VoiceToolSession,
): VoiceToolResult {
  if (!isVoiceToolName(name)) {
    return { ok: false, name: "get_inbox", error: `Unknown desk tool ${name}.` };
  }
  const args = parseArgs(rawArgs);

  switch (name) {
    case "get_inbox":
      return { ok: true, name, data: liveVoiceInbox(snap.inbox) };
    case "get_assignments":
      return { ok: true, name, data: snap.assignments ?? [] };
    case "get_board":
      return {
        ok: true,
        name,
        data: pickSpeakableBoardTrips(snap?.board),
        note: speakableBoardNote(snap?.board),
        source: "desk",
      };
    case "get_fleet":
      return { ok: true, name, data: snap.fleet };
    case "get_mel":
      return { ok: true, name, data: snap.mel };
    case "get_mx":
      return { ok: true, name, data: snap.mx };
    case "get_ledger":
      return { ok: true, name, data: snap.ledger };
    case "get_flight_logs":
      return { ok: true, name, data: { logs: snap.logs, logbook: snap.logbook } };
    case "get_duty":
      return { ok: true, name, data: snap.duty };
    case "get_destinations":
      return {
        ok: true,
        name,
        data: { homeIcao: snap.homeIcao, extraBases: snap.extraBases, destinations: snap.destinations },
      };
    case "get_sync":
      return { ok: true, name, data: snap.sync };
    case "get_tracker":
      return { ok: true, name, data: snap.tracker };
    case "get_aircraft_state":
      return {
        ok: true,
        name,
        data: snap.aircraftState ?? {
          phase: "unknown",
          label: snap.tracker.phase || "No sim",
          note: "No derived aircraft state on this snapshot. Do not invent progress.",
        },
      };
    case "can_assign": {
      const requestId = str(args.requestId);
      const row = requestId ? snap.inbox.find((r) => r.id === requestId) : snap.inbox.find((r) => r.status === "new" || r.status === "assigned");
      const tail =
        findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) }) ??
        snap.fleet.find((a) => a.available);
      if (!row) return { ok: false, name, error: "No inbox charter on file for can_assign." };
      const grounded = tail && (tail.status === "grounded" || tail.status === "in_mx" || !tail.available);
      return {
        ok: true,
        name,
        data: {
          requestId: row.id,
          route: `${row.originIcao}–${row.destIcao}`,
          tail: tail ? { id: tail.id, registration: tail.registration, status: tail.status } : null,
          ok: Boolean(tail && !grounded),
          hard: grounded ? [`${tail!.registration} is not assignable (${tail!.status}).`] : [],
          note: "Desk canAssign (duty / phase / cooldown) is the legal gate. Phone should call the same assign API.",
          jobMode: snap.jobMode ?? "automatic",
        },
      };
    }
    case "get_weather":
      return {
        ok: true,
        name,
        data: {
          unavailable: snap.weatherUnavailable,
          brief: snap.weatherText,
        },
      };
    case "get_notams":
      return {
        ok: true,
        name,
        data: {
          unavailable: snap.weatherUnavailable,
          brief: extractNotamBlock(snap.weatherText),
        },
      };
    case "get_charter_rundown": {
      const charter = findCharterOnDesk(snap, {
        requestId: str(args.requestId),
        originIcao: str(args.originIcao),
        destIcao: str(args.destIcao),
      });
      const trip = findBoardTrip(snap);
      const route = charter
        ? { originIcao: charter.originIcao, destIcao: charter.destIcao }
        : trip
          ? { originIcao: trip.originIcao, destIcao: trip.destIcao }
          : focusRouteFromSnapshot(snap);
      const tailId = charter?.suggestedAircraftId || trip?.aircraftId;
      const tail = tailId ? snap.fleet.find((a) => a.id === tailId) : null;
      const mel = tailId ? snap.mel.filter((m) => m.aircraftId === tailId) : snap.mel;
      if (!charter && !trip) {
        return {
          ok: true,
          name,
          data: {
            onFile: false,
            note: "No charter or board trip on file for that hint. Nothing invented.",
            route,
            weather: snap.weatherText,
            mel,
          },
        };
      }
      return {
        ok: true,
        name,
        data: {
          onFile: true,
          charter,
          trip,
          tail,
          route,
          weather: snap.weatherText,
          weatherUnavailable: snap.weatherUnavailable,
          notams: extractNotamBlock(snap.weatherText),
          mel,
        },
      };
    }
    case "get_current_flight": {
      const current = snap.currentFlight;
      if (!current || current.onFile === false) {
        return {
          ok: true,
          name,
          data: {
            onFile: false,
            note: current?.note || "No current flight on the desk. Do not invent one.",
          },
        };
      }
      return {
        ok: true,
        name,
        data: {
          ...current,
          tracker: snap.tracker,
          aircraftState: snap.aircraftState ?? {
            phase: "unknown",
            label: snap.tracker.phase || "No sim",
            note: "No derived aircraft state on this snapshot. Do not invent progress.",
          },
        },
      };
    }
    case "get_conflicts": {
      const clashes = snap.clashes;
      if (!clashes || clashes.onFile === false) {
        return {
          ok: false,
          name,
          error: clashes?.note || "Conflict list is not on the desk. Do not invent one.",
        };
      }
      const hinted = Boolean(str(args.missionId) || str(args.flightNumber) || str(args.callsign));
      const trip = hinted ? findBoardTrip(snap, tripHintFromArgs(args)) : null;
      if (hinted && !trip) {
        return { ok: false, name, error: "That ticket is not on the desk. Do not invent a conflict." };
      }
      const items = trip
        ? clashes.items.filter((row) => row.missionIds.includes(trip.id) || row.flightNumbers.includes(trip.flightNumber))
        : clashes.items;
      return {
        ok: true,
        name,
        data: {
          onFile: true,
          note: trip
            ? items.length
              ? `${items.length} conflict${items.length === 1 ? "" : "s"} on ${spokenTripLabel(trip)}.`
              : `No conflicts on file for ${spokenTripLabel(trip)}.`
            : clashes.note,
          items,
          duty: snap.duty,
        },
      };
    }
    case "get_trip_support": {
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), {
        requireIdentity: Boolean(str(args.missionId) || str(args.flightNumber) || str(args.callsign) || str(args.route)),
      });
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} Trip Support is not on the desk.` };
      const trip = resolved.trip;
      const lines = Array.isArray(trip.tripSupport) ? trip.tripSupport : [];
      return {
        ok: true,
        name,
        data: {
          onFile: true,
          missionId: trip.id,
          flightNumber: trip.flightNumber || trip.callsign,
          route: trip.route,
          openRequested: trip.tripSupportOpen ?? lines.filter((line) => line.status === "requested").length,
          lines,
          note: lines.length
            ? `${lines.length} Trip Support line${lines.length === 1 ? "" : "s"} on ${spokenTripLabel(trip)}.`
            : `No Trip Support lines on ${spokenTripLabel(trip)}.`,
        },
      };
    }
    case "get_release_pack": {
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), {
        requireIdentity: Boolean(str(args.missionId) || str(args.flightNumber) || str(args.callsign) || str(args.route)),
      });
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} Release pack is not on the desk.` };
      const trip = resolved.trip;
      const pack = trip.releasePack ?? { status: "draft" as const, statusLabel: RELEASE_PACK_STATUS_LABEL.draft };
      const full = missionsFromVoiceBoard(snap).find((m) => m.id === trip.id);
      const view = full
        ? buildReleasePack(full, { aircraftType: snap.fleet.find((a) => a.id === trip.aircraftId)?.icaoType })
        : null;
      return {
        ok: true,
        name,
        data: {
          onFile: true,
          missionId: trip.id,
          flightNumber: trip.flightNumber || trip.callsign,
          route: trip.route,
          status: pack.status,
          statusLabel: pack.statusLabel,
          items: view?.items ?? [],
          note: `${spokenTripLabel(trip)} release pack is ${pack.statusLabel}. Missing checklist items stay missing.`,
        },
      };
    }
    case "get_ofp": {
      const resolved = resolveSimbriefTicket(snap, tripHintFromArgs(args));
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} OFP is not on the desk.` };
      const ofp = resolved.trip.ofp;
      if (!ofp?.onFile || !ofp.originIcao) {
        return {
          ok: true,
          name,
          data: {
            onFile: false,
            missionId: resolved.trip.id,
            flightNumber: resolved.trip.flightNumber || resolved.trip.callsign,
            note: "No OFP on this ticket. Do not invent a number, route, or fuel.",
          },
        };
      }
      return {
        ok: true,
        name,
        data: {
          onFile: true,
          missionId: resolved.trip.id,
          flightNumber: resolved.trip.flightNumber || resolved.trip.callsign,
          originIcao: ofp.originIcao,
          destIcao: ofp.destIcao,
          route: ofp.route,
          match: ofp.match,
          registration: ofp.registration,
          aircraftType: ofp.aircraftType,
          callsign: ofp.callsign,
          note: "OFP already on this ticket. Read only these fields. Do not invent a number.",
        },
      };
    }
    case "list_pending":
      return {
        ok: true,
        name,
        data: {
          pending: (session?.pending ?? []).map((a, index) => ({ index, type: a.type, id: a.id, summary: a.summary })),
          applied: (session?.applied ?? []).map((a) => ({ type: a.type, id: a.id, summary: a.summary })),
        },
      };
    case "propose_create_charter": {
      const route = parseRouteHint({
        originIcao: str(args.originIcao),
        destIcao: str(args.destIcao),
        route: str(args.route),
      });
      const departureIso = parseSpokenIso(str(args.departureIso));
      if (!departureIso) {
        return { ok: false, name, error: "Need a real STD already stated (ISO-8601). Will not invent a time." };
      }
      if (!route.originIcao || !route.destIcao) {
        return { ok: false, name, error: "Need origin and dest ICAO already stated. Will not invent a route." };
      }
      const tail =
        findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) }) ??
        snap.fleet.find((a) => a.available);
      if (!tail) return { ok: false, name, error: "No assignable tail on the desk for that charter." };
      const planned = createManualCharterRequest({
        originIcao: route.originIcao,
        destIcao: route.destIcao,
        departureIso,
        aircraftId: tail.id,
        paxCount: parseNonNegInt(args.paxCount),
        cargoLb: parseNonNegInt(args.cargoLb),
        notes: str(args.notes) || undefined,
        clientName: str(args.clientName) || undefined,
        scenery: sceneryFromVoiceSnap(snap),
        fleet: snap.liveFleet ?? [],
        operatorName: snap.companyName,
        homeIcao: snap.homeIcao,
        extraBases: snap.extraBases,
        scheduleOnlyContentXmlAirports: snap.scheduleOnlyContentXmlAirports,
        preferIcaos: snap.contentXmlIcaos,
      });
      if (!planned.ok) return { ok: false, name, error: planned.error };
      const fileNow = args.fileNow === true;
      return gatedWrite(
        name,
        snap,
        {
          type: "create_charter",
          requestId: planned.request.id,
          originIcao: planned.request.originIcao,
          destIcao: planned.request.destIcao,
          departureIso: planned.request.departureIso,
          aircraftId: tail.id,
          registration: tail.registration,
          paxCount: planned.request.paxCount,
          cargoLb: planned.request.cargoLb,
          notes: str(args.notes) || undefined,
          clientName: planned.request.clientName,
          fileNow,
          summary: fileNow
            ? `Create and file ${planned.request.originIcao}–${planned.request.destIcao} on ${tail.registration} at ${departureIso}.`
            : `Create Inbox charter ${planned.request.originIcao}–${planned.request.destIcao} on ${tail.registration} at ${departureIso}.`,
        },
        session,
      );
    }
    case "propose_mint_on_demand_charter": {
      const route = parseRouteHint({
        originIcao: str(args.originIcao),
        destIcao: str(args.destIcao),
        route: str(args.route),
      });
      const destIcao = route.destIcao || str(args.destIcao).toUpperCase();
      const departureRaw = str(args.departureIso);
      const screened = screenOnDemandCharter({
        destIcao,
        originIcao: route.originIcao || undefined,
        departureIso: departureRaw || undefined,
        techStopIcao: str(args.techStopIcao) || undefined,
        scenery: sceneryFromVoiceSnap(snap),
        scheduleOnlyContentXmlAirports: snap.scheduleOnlyContentXmlAirports,
        preferIcaos: snap.contentXmlIcaos,
      });
      if (!screened.ok) return { ok: false, name, error: screened.error };
      const named = str(args.aircraftId) || str(args.registration);
      const tail = named
        ? findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) })
        : undefined;
      if (named && !tail) return { ok: false, name, error: "That tail is not on the fleet." };
      if (tail && (!tail.available || tail.status === "grounded" || tail.status === "in_mx")) {
        return { ok: false, name, error: `${tail.registration} is not assignable.` };
      }
      const where = screened.originIcao ? `${screened.originIcao}–${screened.destIcao}` : screened.destIcao;
      return gatedWrite(
        name,
        snap,
        {
          type: "mint_on_demand_charter",
          destIcao: screened.destIcao,
          originIcao: screened.originIcao,
          departureIso: screened.departureIso,
          aircraftId: tail?.id,
          registration: tail?.registration,
          paxCount: parseNonNegInt(args.paxCount),
          cargoLb: parseNonNegInt(args.cargoLb),
          notes: str(args.notes) || undefined,
          clientName: str(args.clientName) || undefined,
          techStopIcao: screened.techStopIcao,
          summary: `Mint on-demand charter ${where} onto the schedule. Flight number assigns on confirm.`,
        },
        session,
      );
    }
    case "propose_accept_job": {
      const found = resolveVoiceInboxRow(snap.inbox, inboxHint(args));
      if (!found.ok) return { ok: false, name, error: found.error };
      const row = found.row;
      if (row.kind && row.kind !== "charter") {
        return { ok: false, name, error: "That Inbox id is not a charter on file." };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "accept_job",
          requestId: row.id!,
          summary: `Accept ${spokenInboxRef(row)} and file to the board.`,
        },
        session,
      );
    }
    case "propose_decline_job": {
      const found = resolveVoiceInboxRow(snap.inbox, inboxHint(args));
      if (!found.ok) return { ok: false, name, error: found.error };
      const row = found.row;
      if (row.status === "declined") {
        return { ok: false, name, error: `Inbox ${row.id} is already declined.` };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "decline_job",
          requestId: row.id!,
          summary: `Decline / clear Inbox ${spokenInboxRef(row)}.`,
        },
        session,
      );
    }
    case "propose_clear_inbox": {
      const live = liveVoiceInbox(snap.inbox);
      if (!live.length) {
        return { ok: false, name, error: "Inbox is already clear — no live RFQ on file." };
      }
      const mode = str(args.mode) === "remove" ? "remove" : "decline";
      const ids = live.map((r) => r.id!).filter(Boolean);
      return gatedWrite(
        name,
        snap,
        {
          type: "clear_inbox",
          requestIds: ids,
          mode,
          summary:
            mode === "remove"
              ? `Delete ${ids.length} live Inbox RFQ${ids.length === 1 ? "" : "s"} (desk Delete).`
              : `Clear the live Inbox — decline ${ids.length} RFQ${ids.length === 1 ? "" : "s"}.`,
        },
        session,
      );
    }
    case "propose_remove_inbox": {
      const found = resolveVoiceInboxRow(snap.inbox, inboxHint(args));
      if (!found.ok) return { ok: false, name, error: found.error };
      const row = found.row;
      return gatedWrite(
        name,
        snap,
        {
          type: "remove_inbox",
          requestId: row.id!,
          summary: `Delete Inbox ${spokenInboxRef(row)}.`,
        },
        session,
      );
    }
    case "propose_assign_trip": {
      const found = resolveVoiceInboxRow(snap.inbox, inboxHint(args));
      const row = found.ok ? found.row : null;
      const tail =
        findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) }) ??
        snap.fleet.find((a) => a.available);
      if (!row) return { ok: false, name, error: "No inbox charter on file to assign." };
      if (!tail) return { ok: false, name, error: "No assignable tail on file." };
      if (!tail.available || tail.status === "grounded" || tail.status === "in_mx") {
        return { ok: false, name, error: `${tail.registration} is not assignable (${tail.status}).` };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "assign_trip",
          requestId: row.id!,
          aircraftId: tail.id,
          crewId: str(args.crewId) || undefined,
          summary: `Assign ${row.originIcao}–${row.destIcao} to ${tail.registration}${str(args.crewId) ? ` / ${str(args.crewId)}` : ""} (canAssign + pending pilot accept). One pilot per trip.`,
        },
        session,
      );
    }
    case "propose_assign_tail": {
      const trip = findBoardTrip(snap, { missionId: str(args.missionId) });
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!trip) return { ok: false, name, error: "No board trip on file for that assign." };
      if (!tail) return { ok: false, name, error: "That tail is not on the company fleet." };
      return gatedWrite(
        name,
        snap,
        {
          type: "assign_tail",
          missionId: trip.id,
          aircraftId: tail.id,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `Assign ${tail.registration} to ${trip.callsign} ${trip.route}.`,
        },
        session,
      );
    }
    case "propose_reassign_crew": {
      const trip = findBoardTrip(snap, {
        missionId: str(args.missionId),
        flightNumber: str(args.flightNumber) || str(args.callsign),
        callsign: str(args.callsign),
        originIcao: str(args.originIcao),
        destIcao: str(args.destIcao),
        route: str(args.route),
      });
      if (!trip) return { ok: false, name, error: "No board trip on file for that reassign." };
      const hint = str(args.crewHint) || str(args.crewId);
      if (!hint) return { ok: false, name, error: "Name the company pilot who should take the trip." };
      const callerId = snap.signedOn?.id || snap.caller?.crewId || "";
      const hit = resolveCompanyCrew(snap.crew ?? [], hint, { callerCrewId: callerId });
      if (!hit) {
        return {
          ok: false,
          name,
          error: isSelfCrewHint(hint)
            ? "Caller is not on this company roster."
            : `No crew named ${hint} on this company roster.`,
        };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "reassign_crew",
          missionId: trip.id,
          crewHint: hit.id,
          accept: isSelfCrewHint(hint) || hit.id === callerId,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `Reassign ${trip.flightNumber || trip.callsign} ${trip.originIcao}–${trip.destIcao} to ${hit.displayName}.`,
        },
        session,
      );
    }
    case "propose_schedule_std": {
      const departureIso = parseSpokenIso(str(args.departureIso));
      if (!departureIso) {
        return { ok: false, name, error: "Need a real STD already stated (ISO-8601). Will not invent a time." };
      }
      const boardHinted = Boolean(str(args.missionId) || str(args.callsign));
      const trip = findBoardTrip(snap, { missionId: str(args.missionId), callsign: str(args.callsign) });
      const inboxFound = resolveVoiceInboxRow(snap.inbox, inboxHint(args));
      if (boardHinted) {
        if (!trip) return { ok: false, name, error: "No board trip on file for that STD." };
        return gatedWrite(
          name,
          snap,
          {
            type: "schedule_std",
            missionId: trip.id,
            departureIso,
            requestId: requestIdForTrip(trip.id, snap),
            summary: `Set STD ${departureIso} on ${trip.callsign} ${trip.route}.`,
          },
          session,
        );
      }
      if (hasInboxArgs(args) && inboxFound.ok) {
        return gatedWrite(
          name,
          snap,
          {
            type: "schedule_std",
            departureIso,
            requestId: inboxFound.row.id!,
            summary: `Set STD ${departureIso} on Inbox ${spokenInboxRef(inboxFound.row)}.`,
          },
          session,
        );
      }
      if (trip) {
        return gatedWrite(
          name,
          snap,
          {
            type: "schedule_std",
            missionId: trip.id,
            departureIso,
            requestId: requestIdForTrip(trip.id, snap),
            summary: `Set STD ${departureIso} on ${trip.callsign} ${trip.route}.`,
          },
          session,
        );
      }
      if (inboxFound.ok) {
        return gatedWrite(
          name,
          snap,
          {
            type: "schedule_std",
            departureIso,
            requestId: inboxFound.row.id!,
            summary: `Set STD ${departureIso} on Inbox ${spokenInboxRef(inboxFound.row)}.`,
          },
          session,
        );
      }
      return { ok: false, name, error: "No board trip or Inbox RFQ on file for that STD." };
    }
    case "propose_schedule_eta": {
      const trip = findBoardTrip(snap, { missionId: str(args.missionId), callsign: str(args.callsign) });
      if (!trip) return { ok: false, name, error: "No board trip on file for that ETA." };
      const arrivalIso = parseSpokenIso(str(args.arrivalIso));
      if (!arrivalIso) {
        return { ok: false, name, error: "Need a real STA/ETA already stated (ISO-8601). Will not invent a time." };
      }
      const depMs = Date.parse(trip.departureIso);
      if (Number.isFinite(depMs) && Date.parse(arrivalIso) < depMs) {
        return { ok: false, name, error: "ETA is before STD on file. Will not invent a later departure." };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "schedule_eta",
          missionId: trip.id,
          arrivalIso,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `Set ETA ${arrivalIso} on ${trip.callsign} ${trip.route}.`,
        },
        session,
      );
    }
    case "propose_update_pax": {
      const hinted = Boolean(str(args.missionId) || str(args.callsign));
      const trip = hinted ? findBoardTrip(snap, { missionId: str(args.missionId), callsign: str(args.callsign) }) : null;
      const inboxHit = hasInboxArgs(args) ? resolveVoiceInboxRow(snap.inbox, inboxHint(args)) : null;
      const row = inboxHit?.ok ? inboxHit.row : null;
      if (hinted && !trip && !row) {
        return { ok: false, name, error: "No board trip or Inbox RFQ on file for that load. Will not invent pax." };
      }
      const focused = trip ?? (row ? null : findBoardTrip(snap));
      const targetTrip = trip ?? focused;
      if (!targetTrip && !row) {
        return { ok: false, name, error: "No board trip or Inbox RFQ on file for that load. Will not invent pax." };
      }
      const paxCount = parseNonNegInt(args.paxCount);
      const cargoLb = parseNonNegInt(args.cargoLb);
      if (paxCount === undefined && cargoLb === undefined) {
        return { ok: false, name, error: "Need a stated paxCount and/or cargoLb. Will not invent a load." };
      }
      const label = targetTrip
        ? `${targetTrip.callsign} ${targetTrip.route}`
        : `${row!.originIcao}–${row!.destIcao}`;
      const bits = [
        paxCount !== undefined ? `${paxCount} pax` : "",
        cargoLb !== undefined ? `${cargoLb} lb cargo` : "",
      ].filter(Boolean);
      return gatedWrite(
        name,
        snap,
        {
          type: "update_pax",
          missionId: targetTrip?.id,
          requestId: row?.id ?? (targetTrip ? requestIdForTrip(targetTrip.id, snap) : undefined),
          paxCount,
          cargoLb,
          summary: `Set ${bits.join(" / ")} on ${label}.`,
        },
        session,
      );
    }
    case "propose_update_trip_support": {
      const opRaw = str(args.op).toLowerCase();
      const op = opRaw === "update" || opRaw === "remove" || opRaw === "add" ? opRaw : "";
      if (!op) return { ok: false, name, error: "Need op add, update, or remove. Will not invent a Support write." };
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), { requireIdentity: true });
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} Trip Support is not on the desk.` };
      const trip = resolved.trip;
      const lines = Array.isArray(trip.tripSupport) ? trip.tripSupport : [];
      const lineId = str(args.lineId);
      if ((op === "update" || op === "remove") && !lineId) {
        return { ok: false, name, error: "Need the Trip Support line id already on that ticket." };
      }
      if (op !== "add" && lineId && !lines.some((line) => line.id === lineId)) {
        return { ok: false, name, error: "That Trip Support line is not on the desk." };
      }
      const serviceType = isTripSupportServiceType(str(args.serviceType)) ? (str(args.serviceType) as TripSupportServiceType) : undefined;
      const status = isTripSupportStatus(str(args.status)) ? (str(args.status) as TripSupportStatus) : undefined;
      if (op === "add" && !serviceType && !str(args.airportIcao) && !str(args.vendorName)) {
        return { ok: false, name, error: "Need a service type, airport, or vendor already stated. Will not invent a Support line." };
      }
      const currencyRaw = str(args.currency).toUpperCase();
      const currency =
        currencyRaw === "CAD" || currencyRaw === "USD" || currencyRaw === "EUR" || currencyRaw === "GBP"
          ? currencyRaw
          : undefined;
      const verb = op === "add" ? "Add" : op === "remove" ? "Remove" : "Update";
      const serviceBit = serviceType ? TRIP_SUPPORT_SERVICE_LABEL[serviceType] : "Trip Support";
      return gatedWrite(
        name,
        snap,
        {
          type: "update_trip_support",
          missionId: trip.id,
          op,
          lineId: lineId || undefined,
          airportIcao: str(args.airportIcao) || undefined,
          serviceType,
          vendorName: str(args.vendorName) || undefined,
          status,
          estimatedCost: parseNonNegInt(args.estimatedCost),
          currency,
          notes: str(args.notes) || undefined,
          confirmByIso: parseSpokenIso(str(args.confirmByIso)) || undefined,
          summary: `${verb} ${serviceBit}${str(args.airportIcao) ? ` ${str(args.airportIcao).toUpperCase()}` : ""}${
            str(args.vendorName) ? ` · ${str(args.vendorName)}` : ""
          }${status ? ` (${TRIP_SUPPORT_STATUS_LABEL[status]})` : ""} on ${spokenTripLabel(trip)}.`,
        },
        session,
      );
    }
    case "propose_update_release_pack": {
      const status = isReleasePackStatus(str(args.status)) ? (str(args.status) as ReleasePackStatus) : "";
      if (!status) {
        return { ok: false, name, error: "Need release pack status draft, ready, or released. Will not invent one." };
      }
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), { requireIdentity: true });
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} Release pack is not on the desk.` };
      const trip = resolved.trip;
      return gatedWrite(
        name,
        snap,
        {
          type: "update_release_pack",
          missionId: trip.id,
          status,
          summary: `Set ${spokenTripLabel(trip)} release pack to ${RELEASE_PACK_STATUS_LABEL[status]}.`,
        },
        session,
      );
    }
    case "propose_open_simbrief": {
      const resolved = resolveSimbriefTicket(snap, tripHintFromArgs(args));
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} SimBrief is not opened.` };
      const mission = missionsFromVoiceBoard(snap).find((row) => row.id === resolved.trip.id);
      if (!mission) return { ok: false, name, error: "That ticket is not on the desk. SimBrief is not opened." };
      const generated = simbriefGenerateForMission(mission, {
        fleet: fleetForSimbriefTicket(snap, resolved.trip),
        cargoLb: mission.cargoLb,
      });
      if (!generated.ok) return { ok: false, name, error: generated.error };
      const plan = generated.plan;
      return gatedWrite(
        name,
        snap,
        {
          type: "open_simbrief",
          missionId: mission.id,
          summary: `Open SimBrief generate on the desk PC for ${spokenTripLabel(resolved.trip)}. Prefill UTC STD ${plan.utcLabel}, pax ${plan.pax}, extra cargo ${plan.cargo} (thousands of lb), ${plan.orig}–${plan.dest}, type ${plan.type}, reg ${plan.reg}. No OFP number.`,
        },
        session,
      );
    }
    case "propose_attach_ofp": {
      const resolved = resolveSimbriefTicket(snap, tripHintFromArgs(args));
      if (!resolved.ok) return { ok: false, name, error: `${resolved.error} OFP is not attached.` };
      return gatedWrite(
        name,
        snap,
        {
          type: "attach_ofp",
          missionId: resolved.trip.id,
          summary: `Retrieve the latest OFP for ${spokenTripLabel(resolved.trip)} and attach it to that ticket. No OFP number until SimBrief returns one.`,
        },
        session,
      );
    }
    case "propose_activate_flight": {
      const trip = findBoardTrip(snap, { missionId: str(args.missionId), callsign: str(args.callsign) });
      if (!trip) return { ok: false, name, error: "No board trip on file to activate." };
      if (trip.status === "completed" || trip.status === "cancelled") {
        return { ok: false, name, error: `Trip ${trip.callsign} is ${trip.status} — will not activate.` };
      }
      if (trip.status === "in-progress") {
        return { ok: false, name, error: `Trip ${trip.callsign} is already in progress.` };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "activate_flight",
          missionId: trip.id,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `Activate ${trip.callsign} ${trip.route} — pending to in progress.`,
        },
        session,
      );
    }
    case "propose_update_trip_status": {
      const status = parseVoiceTripStatus(str(args.status));
      if (!status) {
        return { ok: false, name, error: "Need a real board status (pending, in-progress, completed, cancelled). Will not invent one." };
      }
      const hint = tripHintFromArgs(args);
      const resolved = resolveBoardTrip(snap, hint, {
        requireIdentity: status === "cancelled" || status === "pending",
      });
      if (!resolved.ok) return { ok: false, name, error: resolved.error };
      const trip = resolved.trip;
      if (trip.status === status) {
        return { ok: false, name, error: `Trip ${spokenTripLabel(trip)} is already ${status}.` };
      }
      if (status === "pending" && trip.status === "cancelled") {
        return gatedWrite(
          name,
          snap,
          {
            type: "reinstate_trip",
            missionId: trip.id,
            actor: "megan",
            summary: `Reinstate ${spokenTripLabel(trip)} to scheduled.`,
          },
          session,
        );
      }
      if (status === "cancelled") {
        const reason = normalizeCancelReason({
          cancelReason: str(args.cancelReason) || undefined,
          cancelReasonNote: str(args.cancelReasonNote) || undefined,
        });
        if (!reason.ok) {
          return {
            ok: false,
            name,
            error: `${reason.error}. Need cancelReason (operational, scheduling, conflict, ferry_sandwich, or other) — other needs custom text.`,
          };
        }
        return gatedWrite(
          name,
          snap,
          {
            type: "update_trip_status",
            missionId: trip.id,
            status,
            cancelReason: reason.cancelReason,
            cancelReasonNote: reason.cancelReasonNote,
            requestId: requestIdForTrip(trip.id, snap),
            summary: `Cancel ${spokenTripLabel(trip)} — ${reason.cancelReason}.`,
          },
          session,
        );
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "update_trip_status",
          missionId: trip.id,
          status,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `Set ${spokenTripLabel(trip)} to ${status}.`,
        },
        session,
      );
    }
    case "propose_divert_trip": {
      const toIcao = parseDivertIcao(str(args.toIcao) || str(args.divertToIcao));
      if (!toIcao) {
        return { ok: false, name, error: "Need a real divert ICAO. Will not invent one." };
      }
      const reason = shortDivertReason(str(args.reason) || str(args.divertReason));
      if (!reason) {
        return { ok: false, name, error: "Need a short divert reason (what the pilot said)." };
      }
      const phase = String(args.phase ?? "diverting").trim().toLowerCase() === "diverted" ? "diverted" : "diverting";
      const hint = tripHintFromArgs(args);
      const resolved = resolveBoardTrip(snap, hint, { requireIdentity: true });
      if (!resolved.ok) return { ok: false, name, error: resolved.error };
      const trip = resolved.trip;
      if (trip.status === "cancelled" || trip.status === "completed") {
        return { ok: false, name, error: `Trip ${spokenTripLabel(trip)} is ${trip.status} — will not divert.` };
      }
      const filed = String(trip.destIcao ?? "").trim().toUpperCase();
      if (filed && toIcao === filed) {
        return { ok: false, name, error: `${toIcao} is the filed dest — not a diversion.` };
      }
      const verb = phase === "diverted" ? "Diverted" : "Diverting";
      return gatedWrite(
        name,
        snap,
        {
          type: "divert_trip",
          missionId: trip.id,
          toIcao,
          reason,
          phase,
          requestId: requestIdForTrip(trip.id, snap),
          summary: `${verb} ${spokenTripLabel(trip)} to ${toIcao} — ${reason}. Intended dest stays ${filed || trip.destIcao || "on file"}.`,
        },
        session,
      );
    }
    case "propose_reinstate_trip": {
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), { requireIdentity: true });
      if (!resolved.ok) return { ok: false, name, error: resolved.error };
      const trip = resolved.trip;
      if (trip.status !== "cancelled") {
        return { ok: false, name, error: `${spokenTripLabel(trip)} is ${trip.status} — nothing to reinstate.` };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "reinstate_trip",
          missionId: trip.id,
          actor: "megan",
          summary: `Reinstate ${spokenTripLabel(trip)} to scheduled.`,
        },
        session,
      );
    }
    case "propose_schedule_ferry": {
      const route = parseRouteHint({
        originIcao: str(args.originIcao),
        destIcao: str(args.destIcao),
        route: str(args.route),
      });
      const xmlBlock = contentXmlScheduleBlocked(
        Boolean(snap.scheduleOnlyContentXmlAirports),
        snap.contentXmlIcaos,
        [route.originIcao, route.destIcao],
      );
      if (xmlBlock) return { ok: false, name, error: xmlBlock };
      const planned = planOpsFerry({
        originIcao: route.originIcao ?? "",
        destIcao: route.destIcao ?? "",
        departureIso: str(args.departureIso) ?? "",
        missions: missionsFromVoiceBoard(snap),
        fleet: snap.liveFleet ?? [],
        scenery: sceneryFromVoiceSnap(snap),
        aircraftId: str(args.aircraftId),
        registration: str(args.registration),
        crewId: str(args.crewId),
        forceCreate: args.forceCreate === true,
      });
      if (!planned.ok) return { ok: false, name, error: planned.error };
      if (planned.mode === "reinstate") {
        return gatedWrite(
          name,
          snap,
          {
            type: "schedule_ferry",
            mode: "reinstate",
            missionId: planned.mission.id,
            originIcao: planned.mission.originIcao,
            destIcao: planned.mission.destIcao,
            departureIso: planned.mission.departureIso,
            aircraftId: planned.mission.aircraftId,
            registration: planned.mission.registration,
            summary: planned.summary,
          },
          session,
        );
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "schedule_ferry",
          mode: "create",
          originIcao: planned.originIcao,
          destIcao: planned.destIcao,
          departureIso: planned.departureIso,
          aircraftId: planned.spec.id,
          registration: planned.spec.registration,
          crewId: planned.crewId,
          summary: planned.summary,
        },
        session,
      );
    }
    case "propose_ferry_to_revenue": {
      const resolved = resolveBoardTrip(snap, tripHintFromArgs(args), { requireIdentity: true });
      if (!resolved.ok) return { ok: false, name, error: resolved.error };
      const trip = resolved.trip;
      const pax = num(args.paxCount);
      if (pax === undefined || pax < 1) {
        return { ok: false, name, error: "Need a pax count of at least 1 to convert a ferry to revenue." };
      }
      if ((trip.flightType ?? "") !== "ferry" || !isOpenOpsFerry({ flightType: "ferry", status: trip.status, isMaintenance: trip.isMaintenance })) {
        return {
          ok: false,
          name,
          error: `${spokenTripLabel(trip)} is ${spokenFlightKind(trip)} — only an open ferry takes last-minute pax.`,
        };
      }
      const note = str(args.note) || undefined;
      return gatedWrite(
        name,
        snap,
        {
          type: "ferry_to_revenue",
          missionId: trip.id,
          paxCount: Math.round(pax),
          note,
          summary: ferryToRevenueConfirmCopy({
            label: spokenTripLabel(trip),
            paxCount: Math.round(pax),
            factor: FERRY_REVENUE_QUOTE_FACTOR,
          }),
        },
        session,
      );
    }
    case "propose_remove_trip": {
      const trip = findBoardTrip(snap, { missionId: str(args.missionId), callsign: str(args.callsign) });
      if (!trip) return { ok: false, name, error: "No board trip on file to delete." };
      return gatedWrite(
        name,
        snap,
        {
          type: "remove_trip",
          missionId: trip.id,
          summary: `Delete ${trip.callsign} ${trip.route} from the board.`,
        },
        session,
      );
    }
    case "propose_clear_flight_log": {
      const raw = Array.isArray(args.keep) ? (args.keep as unknown[]).map((v) => str(v)) : str(args.keep);
      const keep = parseFlightLogKeepList(raw.length ? raw : DEFAULT_FLIGHT_LOG_KEEP);
      if (!keep.length) {
        return {
          ok: false,
          name,
          error: "Need the flight numbers that stay. Say which trips to keep and I will clear the rest.",
        };
      }
      // The desk recomputes which rows go at apply time — Megan never names them.
      return gatedWrite(
        name,
        snap,
        {
          type: "clear_flight_log",
          keep,
          summary: `Clear the flight log and flight plans except ${keep.join(
            " and ",
          )}. Everything else is deleted and cannot be restored.`,
        },
        session,
      );
    }
    case "propose_respond_assignment": {
      const assignment = findAssignment(snap, { assignmentId: str(args.assignmentId), requestId: str(args.requestId) });
      if (!assignment) return { ok: false, name, error: "That assignment is not on file." };
      const status = parseAssignmentStatus(str(args.status));
      if (!status) return { ok: false, name, error: "Need accepted, declined, or held." };
      const reasonCode = str(args.reasonCode) || (status === "held" ? "defer" : "");
      if ((status === "accepted" || status === "declined") && !reasonCode) {
        return { ok: false, name, error: "Need a reasonCode (cannot_fly_time, weather, aircraft, personal, defer, other)." };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "respond_assignment",
          assignmentId: assignment.id,
          status,
          reasonCode: reasonCode || "other",
          comment: str(args.comment) || undefined,
          summary: `${status} assignment ${assignment.registration || assignment.id} (${reasonCode || "defer"}).`,
        },
        session,
      );
    }
    case "propose_file_mel": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      const description = str(args.description);
      if (!description) return { ok: false, name, error: "Need a MEL description already stated." };
      const severity = parseMelSeverity(str(args.severity)) || "defer";
      return gatedWrite(
        name,
        snap,
        {
          type: "file_mel",
          aircraftId: tail.id,
          description,
          severity,
          ata: str(args.ata) || undefined,
          summary: `File ${severity} MEL on ${tail.registration}: ${description}`,
        },
        session,
      );
    }
    case "propose_close_mel": {
      const mel = findMel(snap, { melId: str(args.melId), aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!mel) return { ok: false, name, error: "That MEL is not on file." };
      return gatedWrite(name, snap, { type: "close_mel", melId: mel.id, summary: `Close MEL ${mel.description || mel.id}.` }, session);
    }
    case "propose_file_squawk": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      const title = str(args.title);
      if (!title) return { ok: false, name, error: "Need a squawk title already stated." };
      const severity = parseMelSeverity(str(args.severity)) || "info";
      return gatedWrite(
        name,
        snap,
        {
          type: "file_squawk",
          aircraftId: tail.id,
          title,
          severity,
          detail: str(args.detail) || undefined,
          summary: `File ${severity} squawk on ${tail.registration}: ${title}`,
        },
        session,
      );
    }
    case "propose_close_squawk": {
      const squawk = findSquawk(snap, { squawkId: str(args.squawkId), aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!squawk) return { ok: false, name, error: "That squawk is not on file." };
      return gatedWrite(name, snap, { type: "close_squawk", squawkId: squawk.id, summary: `Close squawk ${squawk.title || squawk.id}.` }, session);
    }
    case "propose_send_to_mx": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      return gatedWrite(
        name,
        snap,
        {
          type: "send_to_mx",
          aircraftId: tail.id,
          mroId: str(args.mroId) || undefined,
          summary: `Send ${tail.registration} to mx${str(args.mroId) ? ` (${str(args.mroId)})` : ""}.`,
        },
        session,
      );
    }
    case "propose_close_mx": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      return gatedWrite(name, snap, { type: "close_mx", aircraftId: tail.id, summary: `Close mx on ${tail.registration} — return to the line.` }, session);
    }
    case "propose_fleet_status": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      const status = parseFleetStatus(str(args.status));
      if (!status) return { ok: false, name, error: "Need a real fleet status (available, grounded, ferry, in_mx)." };
      return gatedWrite(
        name,
        snap,
        {
          type: "fleet_status",
          aircraftId: tail.id,
          status,
          summary: `Set ${tail.registration} to ${status}.`,
        },
        session,
      );
    }
    case "propose_arm_standby":
      return gatedWrite(name, snap, { type: "arm_standby", summary: "Arm Duty Standby." }, session);
    case "propose_disarm_standby":
      return gatedWrite(name, snap, { type: "disarm_standby", summary: "Disarm Duty Standby." }, session);
    case "propose_mel_ground": {
      const tail = findTail(snap, { aircraftId: str(args.aircraftId), registration: str(args.registration) });
      if (!tail) return { ok: false, name, error: "Tail not on file — will not invent an airframe." };
      const description = str(args.description) || "Grounding MEL from dispatch voice.";
      return gatedWrite(
        name,
        snap,
        {
          type: "apply_mel_ground",
          aircraftId: tail.id,
          description,
          ata: str(args.ata) || undefined,
          summary: `File grounding MEL on ${tail.registration}: ${description}`,
        },
        session,
      );
    }
    case "propose_capital_adjust": {
      const amount = Number(args.amountCad);
      if (!Number.isFinite(amount) || amount === 0) {
        return { ok: false, name, error: "Need a non-zero amount already stated. Ledger will not invent cash." };
      }
      const memo = str(args.memo) || "Voice-proposed ledger adjustment";
      return gatedWrite(
        name,
        snap,
        {
          type: "change_capital",
          amountCad: Math.round(amount * 100) / 100,
          memo,
          summary: `Post ledger adjustment ${amount >= 0 ? "+" : ""}${amount} CAD — ${memo}. Desk balance on file is ${snap.ledger.balanceLabel}.`,
        },
        session,
      );
    }
    case "propose_push_sync":
      if (!snap.sync.configured) {
        return { ok: false, name, error: "Sync is not configured on this desk." };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "push_sync",
          summary: `Push desk snapshot to ${snap.sync.label}.`,
        },
        session,
      );
    case "propose_pull_sync":
      if (!snap.sync.configured) {
        return { ok: false, name, error: "Sync is not configured on this desk." };
      }
      return gatedWrite(
        name,
        snap,
        {
          type: "pull_sync",
          summary: `Pull the Nanode company snapshot onto this desk (${snap.sync.label}). Tickets stay the remote board. Local keys stay.`,
        },
        session,
      );
    case "confirm_pending": {
      const blocked = requireSpokenYes(name, args);
      if (blocked) return blocked;
      const out = confirmSessionPending(session, snap, {
        index: num(args.index),
        type: str(args.type) || undefined,
        id: str(args.id) || undefined,
      });
      if (!out.ok) return { ok: false, name, error: out.error };
      return {
        ok: true,
        name,
        applied: true,
        action: out.action,
        actions: [out.action],
        data: { applied: true, summary: out.action.summary, action: out.action },
      };
    }
    case "apply_pending": {
      const blocked = requireSpokenYes(name, args);
      if (blocked) return blocked;
      const oneShot = typeof args.index === "number" || Boolean(str(args.type) || str(args.id));
      if (oneShot) {
        const out = confirmSessionPending(session, snap, {
          index: num(args.index),
          type: str(args.type) || undefined,
          id: str(args.id) || undefined,
        });
        if (!out.ok) return { ok: false, name, error: out.error };
        return {
          ok: true,
          name,
          applied: true,
          action: out.action,
          actions: [out.action],
          data: { applied: true, summary: out.action.summary, action: out.action },
        };
      }
      const out = applyAllSessionPending(session, snap);
      if (!out.ok) return { ok: false, name, error: out.error };
      return {
        ok: true,
        name,
        applied: true,
        action: out.actions[out.actions.length - 1],
        actions: out.actions,
        data: { applied: true, count: out.actions.length, summaries: out.actions.map((a) => a.summary) },
      };
    }
    case "reject_pending": {
      const out = rejectSessionPending(session, {
        index: num(args.index),
        type: str(args.type) || undefined,
        id: str(args.id) || undefined,
      });
      if (!out.ok) return { ok: false, name, error: out.error };
      return { ok: true, name, action: out.action, data: { rejected: true, summary: out.action.summary } };
    }
    default:
      return { ok: false, name, error: "Tool not wired." };
  }
}

export function collectPendingActions(results: VoiceToolResult[]): VoicePendingAction[] {
  return results.filter((r) => r.requiresConfirmation && r.action).map((r) => r.action!);
}

export function formatToolResultForModel(result: VoiceToolResult): string {
  if (!result.ok) {
    return JSON.stringify({
      ok: false,
      error: result.error,
      note: result.note || "Say that you could not get it. Do not sit silent.",
    });
  }
  if (result.applied && (result.action || result.actions?.length)) {
    return JSON.stringify({
      ok: true,
      applied: true,
      action: result.action,
      actions: result.actions,
      note: "Spoken confirmation applied the desk write. You may tell the caller it is on the board.",
    });
  }
  if (result.requiresConfirmation && result.action) {
    return JSON.stringify({
      ok: true,
      requiresConfirmation: true,
      action: result.action,
      note: "Do not claim the write happened. Get a spoken yes from the caller, then call confirm_pending with spokenYes true.",
    });
  }
  return JSON.stringify({
    ok: true,
    data: result.data,
    ...(result.note ? { note: result.note } : {}),
    ...(result.source ? { source: result.source } : {}),
  });
}

function inboxHint(args: Record<string, unknown>): VoiceInboxHint {
  return {
    requestId: str(args.requestId),
    client: str(args.client),
    originIcao: str(args.originIcao),
    destIcao: str(args.destIcao),
    route: str(args.route),
    subject: str(args.subject),
  };
}

function hasInboxArgs(args: Record<string, unknown>): boolean {
  return Boolean(
    str(args.requestId) || str(args.client) || str(args.originIcao) || str(args.destIcao) || str(args.route) || str(args.subject),
  );
}

function parseAssignmentStatus(raw: string): VoiceAssignmentStatus | "" {
  const key = raw.trim().toLowerCase();
  if (key === "accept" || key === "accepted") return "accepted";
  if (key === "decline" || key === "declined") return "declined";
  if (key === "hold" || key === "held" || key === "defer") return "held";
  return "";
}

function parseMelSeverity(raw: string): VoiceMelSeverity | "" {
  const key = raw.trim().toLowerCase();
  if (key === "info" || key === "defer" || key === "ground") return key;
  if (key === "grounding" || key === "aog") return "ground";
  return "";
}

function parseFleetStatus(raw: string): VoiceFleetStatus | "" {
  const key = raw.trim().toLowerCase().replace(/-/g, "_");
  if (key === "available" || key === "line") return "available";
  if (key === "grounded" || key === "ground") return "grounded";
  if (key === "ferry") return "ferry";
  if (key === "in_mx" || key === "mx" || key === "maintenance") return "in_mx";
  return "";
}

function findAssignment(snap: VoiceDeskSnapshot, hint: { assignmentId?: string; requestId?: string }) {
  const list = snap.assignments ?? [];
  if (hint.assignmentId) {
    const hit = list.find((a) => a.id === hint.assignmentId);
    if (hit) return hit;
  }
  if (hint.requestId) {
    const hit = list.find((a) => a.requestId === hint.requestId && !String(a.status ?? "").includes("superseded"));
    if (hit) return hit;
  }
  return list.find((a) => a.status === "pending") ?? null;
}

function findMel(snap: VoiceDeskSnapshot, hint: { melId?: string; aircraftId?: string; registration?: string }) {
  if (hint.melId) {
    const hit = snap.mel.find((m) => m.id === hint.melId);
    if (hit) return hit;
  }
  const tail = findTail(snap, { aircraftId: hint.aircraftId, registration: hint.registration });
  if (tail) return snap.mel.find((m) => m.aircraftId === tail.id) ?? null;
  return snap.mel[0] ?? null;
}

function findSquawk(snap: VoiceDeskSnapshot, hint: { squawkId?: string; aircraftId?: string; registration?: string }) {
  const list = snap.mx?.squawks ?? [];
  if (hint.squawkId) {
    const hit = list.find((s) => s.id === hint.squawkId);
    if (hit) return hit;
  }
  const tail = findTail(snap, { aircraftId: hint.aircraftId, registration: hint.registration });
  if (tail) return list.find((s) => s.aircraftId === tail.id) ?? null;
  return list[0] ?? null;
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function num(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return undefined;
}

function parseSpokenIso(raw: string): string {
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString();
}

function parseNonNegInt(value: unknown): number | undefined {
  const n = num(value);
  if (n === undefined || n < 0 || !Number.isFinite(n)) return undefined;
  return Math.round(n);
}

function requestIdForTrip(missionId: string, snap: VoiceDeskSnapshot): string | undefined {
  if (missionId.startsWith("voice-")) return missionId.slice("voice-".length);
  const trip = snap.board.find((m) => m.id === missionId);
  if (!trip) return undefined;
  const hit = snap.inbox.find(
    (r) => r.originIcao === trip.originIcao && r.destIcao === trip.destIcao && (r.status === "accepted" || r.status === "new"),
  );
  return hit?.id;
}

function extractNotamBlock(weatherText: string): string {
  const idx = weatherText.search(/\nNOTAM\b|\nTFR\b/);
  if (idx === -1) return weatherText.includes("NOTAM") ? weatherText : "No NOTAM / TFR block on the live brief.";
  return weatherText.slice(idx).trim();
}

export function melLineForTail(snap: VoiceDeskSnapshot, aircraftId?: string): string {
  const rows = aircraftId ? snap.mel.filter((m) => m.aircraftId === aircraftId) : snap.mel;
  if (!rows.length) return "MEL clean — no open deferred items on file.";
  return rows
    .map((m) => `${m.registration || m.aircraftId} ${m.ata || "ATA—"} ${MEL_SEVERITY_LABEL[m.severity]} — ${m.description}`)
    .join(" / ");
}
